import { useTagBlocksStore } from "entities/modules/stores/useTagBlocksStore";
import { TagMatchingType } from "entities/modules/types";
import { ModuleTagType } from "entities/tagTypes/types";
import { FC } from "react";
import ContentSection from "shared/components/Drawer/components/ContentSection";
import TagGroup from "widgets/ModuleTagsWidget/components/TagGroup";
import { useMetadataTagsOptions } from "widgets/ModuleTagsWidget/hooks/useMetadataTagsOptions";
import { useServiceTagsOptions } from "widgets/ModuleTagsWidget/hooks/useServiceTagsOptions";
import { useThemeTagsOptions } from "widgets/ModuleTagsWidget/hooks/useThemeTagsOptions";
import { TagOption } from "widgets/ModuleTagsWidget/type";
import { useShallow } from "zustand/react/shallow";
import { tagTypeOptions } from "../TagGroup/constants";

type Props = {
  id: string;
  tagType: ModuleTagType | null;
  primaryTagId: string | null;
  selectedTags: TagOption[];
  deletable: boolean;
};

const TagTypeItem: FC<Props> = ({
  id,
  tagType,
  primaryTagId,
  selectedTags,
  deletable,
}) => {
  const {
    tagBlocks,
    deleteTagBlock,
    changeTagType,
    changeTags,
    changePrimaryTag,
    changeMatchingType,
  } = useTagBlocksStore(
    useShallow((state) => ({
      tagBlocks: state.tagBlocks,
      deleteTagBlock: state.deleteTagBlock,
      changeTagType: state.changeTagType,
      changePrimaryTag: state.changePrimaryTag,
      changeTags: state.changeTags,
      changeMatchingType: state.changeMatchingType,
    })),
  );

  const { servicePrimaryTagOptions, secondaryServiceTags } =
    useServiceTagsOptions({
      id,
      primaryTagId,
      tagBlocks: tagBlocks,
    });

  const { secondaryThemeTags, themePrimaryTagOptions } = useThemeTagsOptions({
    id,
    primaryTagId,
    tagBlocks: tagBlocks,
  });

  const { metadataPrimaryTagOptions, metadataSecondaryTagOptions } =
    useMetadataTagsOptions({
      id,
      primaryTagId,
      tagBlocks: tagBlocks,
    });

  const handleChangeTagType = (tagTypeId: ModuleTagType) => {
    changeTagType(id, tagTypeId);
  };

  const handleChangeTags = (newTags: TagOption[]) => {
    changeTags(id, newTags);
  };

  const handleDeleteTagBlock = () => {
    deleteTagBlock(id);
  };

  const handleChangePrimaryTag = (primaryTagId: string) => {
    changePrimaryTag(id, primaryTagId);
  };

  const handleChangeMatchingType = (matchingType: TagMatchingType) => {
    changeMatchingType(id, matchingType);
  };

  const primaryOptions = {
    [ModuleTagType.METADATA]: metadataPrimaryTagOptions,
    [ModuleTagType.SERVICE]: servicePrimaryTagOptions,
    [ModuleTagType.THEME]: themePrimaryTagOptions,
  };

  const secondaryOptions = {
    [ModuleTagType.METADATA]: metadataSecondaryTagOptions,
    [ModuleTagType.SERVICE]: secondaryServiceTags,
    [ModuleTagType.THEME]: secondaryThemeTags,
  };

  const matchingType = tagBlocks.find((block) => block.id === id)?.matchingType;
  const isVisibleMatchingType =
    tagBlocks.length > 1 &&
    tagBlocks.findIndex((block) => block.id === id) !== 0;

  return (
    <ContentSection>
      <TagGroup<ModuleTagType>
        selectedTagType={tagType}
        selectedPrimaryTagId={primaryTagId}
        tagTypeOptions={tagTypeOptions}
        selectedTags={selectedTags}
        secondaryTagOptions={secondaryOptions[tagType ?? ModuleTagType.SERVICE]}
        onChangePrimaryTag={handleChangePrimaryTag}
        onDeleteTagBlock={handleDeleteTagBlock}
        onSecondaryTagsChange={handleChangeTags}
        primaryTagOptions={primaryOptions[tagType ?? ModuleTagType.SERVICE]}
        onTagTypeChange={handleChangeTagType}
        deletable={deletable}
        isLoadingTags={false}
        matchingType={matchingType}
        onMatchingTypeChange={handleChangeMatchingType}
        isVisibleMatchingType={isVisibleMatchingType}
      />
    </ContentSection>
  );
};

export default TagTypeItem;
